import { reactive } from 'vue';
import axios from 'axios';
import moment from 'moment';
import DriveBookingClient from '../client/DriveBookingClient';
import SyncResponse from '../models/SyncResponse';
import DriveBookingContext from '../models/DriveBookingContext';
import { isPresent } from '@/shared/utils/typescript';
import ContactContext from '@/contact/models/ContactContext';
import DriveBookingUpdate from '../models/DriveBookingUpdate';
import Dossier from '@/dossier/models/Dossier';
import { DossierId } from '@/dossier/models/AbstractDossier';
import DriveAvailableAssets from '../models/DriveAvailableAssets';
import DriveBookingStatus from '../constants/DriveBookingStatus';
import DriveBooking from '../models/DriveBooking';

class DriveBookingService {
  private cancelTokens: Map<number, any> = new Map();

  async loadForDossier(contactContext: ContactContext, dossier: Dossier) {
    const context = reactive(new DriveBookingContext(contactContext, dossier)) as DriveBookingContext;
    context.driveBookings = await DriveBookingClient.loadForDossier(dossier.id);
    return context;
  }

  async loadDriveBookings(dossierId: DossierId, context: DriveBookingContext) {
    context.driveBookings = await DriveBookingClient.loadForDossier(dossierId);
  }

  async loadDetails(driveBookingId: number, context: DriveBookingContext) {
    if (isPresent(driveBookingId)) {
      context.selectedDriveBooking = await DriveBookingClient.loadDetails(driveBookingId);
    } else {
      context.selectedDriveBooking = null;
    }
  }

  async syncDriveBooking(context: DriveBookingContext, driveBookingId: number, shouldSyncDetails = false): Promise<{
    syncResult: SyncResponse;
    updated: boolean;
  }> {
    this.cancelOngoingSyncForBooking(driveBookingId);

    const cancelTokenSource = axios.CancelToken.source();
    this.cancelTokens.set(driveBookingId, cancelTokenSource);

    try {
      const result = await DriveBookingClient.syncDriveBooking(driveBookingId, cancelTokenSource);
      this.cancelTokens.delete(driveBookingId);

      let updated = false;

      if (result.synchronizationSuccessful) {
        if (result.dataHasChanged) {
          updated = shouldSyncDetails
            ? await this.loadDetailsAndMarkUpdated(driveBookingId, context)
            : await this.updateSingleBooking(context, driveBookingId);
        } else {
          updated = this.updateSynchronizationInfo(context, driveBookingId, result, shouldSyncDetails);
        }
      }

      return {
        syncResult: result,
        updated,
      };
    } catch (error) {
      this.cancelTokens.delete(driveBookingId);
      throw error;
    }
  }

  getUpcomingBooking(driveBookings: DriveBooking[]): DriveBooking | null {
    const now = moment();

    const validBookings = driveBookings.filter(
      (booking) => booking.status !== DriveBookingStatus.CANCELED
        && booking.status !== DriveBookingStatus.FINISHED
        && booking.status !== DriveBookingStatus.NO_SHOW,
    );

    if (validBookings.length === 0) {
      return null;
    }

    const futureBookings = validBookings.filter(
      (booking) => booking.scheduledFrom && moment(booking.scheduledFrom).isAfter(now),
    );

    if (futureBookings.length > 0) {
      return futureBookings.sort((a, b) => {
        if (!a.scheduledFrom || !b.scheduledFrom) return 0;
        return a.scheduledFrom.valueOf() - b.scheduledFrom.valueOf();
      })[0];
    }

    return validBookings
      .filter(booking => booking.scheduledFrom)
      .sort((a, b) => {
        if (!a.scheduledFrom || !b.scheduledFrom) return 0;
        return b.scheduledFrom.valueOf() - a.scheduledFrom.valueOf();
      })[0] || null;
  }

  private async loadDetailsAndMarkUpdated(driveBookingId: number, context: DriveBookingContext): Promise<boolean> {
    await this.loadDetails(driveBookingId, context);
    return true;
  }

  private updateSynchronizationInfo(
    context: DriveBookingContext,
    driveBookingId: number,
    result: SyncResponse,
    shouldSyncDetails: boolean,
  ): boolean {
    if (shouldSyncDetails && context.selectedDriveBooking) {
      context.selectedDriveBooking.updateSynchronizationInfo(
        result.lastSynchronizedOn,
        result.lastSynchronizedBy,
      );
      return true;
    }

    const index = context.driveBookings.findIndex(booking => booking.id === driveBookingId);
    if (index !== -1) {
      context.driveBookings[index].updateSynchronizationInfo(
        result.lastSynchronizedOn,
        result.lastSynchronizedBy,
      );
      return true;
    }

    return false;
  }

  private async updateSingleBooking(context: DriveBookingContext, driveBookingId: number): Promise<boolean> {
    try {
      const updatedBooking = await DriveBookingClient.getById(driveBookingId);
      if (!updatedBooking) {
        return false;
      }

      const index = context.driveBookings.findIndex(booking => booking.id === driveBookingId);
      if (index === -1) {
        return false;
      } else {
        context.driveBookings.splice(index, 1, updatedBooking);
        return true;
      }
    } catch {
      return false;
    }
  }

  cancelOngoingSyncForBooking(driveBookingId: number): void {
    if (this.cancelTokens.has(driveBookingId)) {
      this.cancelTokens.get(driveBookingId)?.cancel('Operation canceled due to new request');
      this.cancelTokens.delete(driveBookingId);
    }
  }

  cancelAllSyncs(): void {
    this.cancelTokens.forEach(source => source.cancel('Operation canceled due to navigation'));
    this.cancelTokens.clear();
  }

  async getAvailableAssets(startDate: string, endDate: string, fleetSourceId: number) {
    try {
      const driveAvailableAssets = await DriveBookingClient.getAvailableAssets(startDate, endDate, fleetSourceId);
      if (!driveAvailableAssets) {
        return new DriveAvailableAssets();
      }

      return reactive(driveAvailableAssets);
    } catch (error) {
      return new DriveAvailableAssets();
    }
  }

  async updateBillings(driveBookingId: number, data: DriveBookingUpdate): Promise<void> {
    await DriveBookingClient.update(driveBookingId, data);
  }

  async updateBookingStatus(driveBookingId: number, status: number) {
    const bookingStatus = await DriveBookingClient.putDriveBookingStatus(driveBookingId, status);
    return bookingStatus;
  }
}

export default new DriveBookingService();
